import type { ErrorInfo, HydrationOptions } from 'react-dom/client';
import { addErrorForHydrationStats } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import {
	extractOwnershipStack,
	getContainingPackageName,
	getOwningTeam,
} from '@jira/platform__ownership-marker/src/ui/extractors.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { getRouterState } from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';

import { captureException as reportToSentry } from '@atlassian/jira-capture-exception/src/index.tsx';

const location = 'react-hydrate-root';

export const hydrationOptions: HydrationOptions = {
	onRecoverableError: (untypedError, errorInfo) => {
		if (errorInfo.digest === 'ignore') {
			// the server has told us to ignore this error
			return;
		}

		const error = toTypedError(untypedError);
		const ownershipMetadata = extractOwnershipMetadata(errorInfo);

		let routeMetadata = {};
		if (fg('jfp-magma-log-hydration-errors-route-name')) {
			routeMetadata = getRouteMetadata();
		}
		if (fg('jfp-magma-log-hydration-errors')) {
			logToSplunk(error, errorInfo, { ...ownershipMetadata, ...routeMetadata });
			reportToSentry(location, error, { ...errorInfo, ...ownershipMetadata });
		}

		if (fg('jfp-magma-3742-report-hydration-stats')) {
			addErrorForHydrationStats(error, errorInfo);
		}

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__ = window.__HYDRATION_RECOVERABLE_ERRORS__ ?? [];
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__HYDRATION_RECOVERABLE_ERRORS__.push({
			error,
			errorInfo,
			ownershipMetadata,
			routeMetadata,
		});
	},
};

const toTypedError = (untypedError: unknown) => {
	if (untypedError instanceof Error) {
		return untypedError;
	}
	try {
		return new Error(`Unknown error occurred during hydration: ${JSON.stringify(untypedError)}`);
	} catch (e) {
		return new Error(String(untypedError));
	}
};

type OwnershipMetadata = { teamName?: string; packageName?: string; ownershipStack?: string };
const extractOwnershipMetadata = ({ componentStack }: ErrorInfo): OwnershipMetadata => {
	if (!componentStack) {
		return {};
	}

	const ownershipStack = extractOwnershipStack(componentStack);
	const result: OwnershipMetadata = {
		ownershipStack: JSON.stringify(ownershipStack),
	};

	const teamName = getOwningTeam(ownershipStack);
	if (teamName) {
		result.teamName = teamName;
	}
	const packageName = getContainingPackageName(ownershipStack);
	if (packageName) {
		result.packageName = packageName;
	}
	return result;
};

type RouteMetadata = { routeName?: string };
const getRouteMetadata = (): RouteMetadata => {
	const routerState = getRouterState();
	if (routerState?.route?.name) {
		return { routeName: routerState.route.name };
	}

	return {};
};

type Metadata = OwnershipMetadata & RouteMetadata;
const logToSplunk = (error: Error, errorInfo: ErrorInfo, metadata: Metadata) => {
	const { componentStack, digest } = errorInfo;
	const event: Record<string, string | undefined> = { digest, ...metadata };
	if (componentStack) {
		const lines = componentStack.trim().split('\n');
		event.shortenedComponentStack = [...lines.slice(0, 10), '...', ...lines.slice(-3)].join('\n');
	}

	log.safeErrorWithoutCustomerData(location, `onRecoverableError: ${error.message}`, event);
};
